import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { getAnalyticsTrends } from '../../api/client';
import type { TrendsAnalyticsResponse } from '../../api/types';
import type { AnalyticsGranularity, AnalyticsPeriod } from '../../api/types';
import { AnalyticsQueryState } from '../../analytics/AnalyticsQueryState';
import { PeriodGranularityControls } from '../../analytics/PeriodGranularityControls';
import { SimpleColumnChart } from '../../analytics/SimpleColumnChart';
import { formatPercent, formatUsdFromCents } from '../../analytics/formatAnalytics';
import { parseGranularity, parsePeriod } from '../../analytics/analyticsParams';
import * as styles from './analyticsPageStyles';

export function TrendsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const period = parsePeriod(searchParams.get('period'));
  const granularity = parseGranularity(searchParams.get('granularity'));

  const setPeriod = useCallback(
    (p: AnalyticsPeriod) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('period', String(p));
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const setGranularity = useCallback(
    (g: AnalyticsGranularity) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('granularity', g);
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams]
  );

  const [data, setData] = useState<TrendsAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    getAnalyticsTrends({ period, granularity })
      .then(setData)
      .catch((e) => {
        setError(e instanceof Error ? e.message : 'Unable to load trends data. Please try again.');
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [period, granularity]);

  useEffect(() => {
    load();
  }, [load]);

  const adoption = data?.adoption ?? [];
  const payments = data?.payments ?? [];
  const usage = data?.usage ?? [];
  const prior = data?.priorPeriodComparison;

  const isEmpty = adoption.length === 0 && payments.length === 0 && usage.length === 0;

  const adoptionChart = adoption.map((p) => ({
    key: p.bucket,
    label: p.bucket.length > 10 ? p.bucket.slice(5) : p.bucket,
    value: p.newInstalls ?? 0,
  }));

  const revenueChart = payments.map((p) => ({
    key: p.bucket,
    label: p.bucket.length > 10 ? p.bucket.slice(5) : p.bucket,
    value: p.revenue ?? 0,
  }));

  const tracksChart = usage.map((p) => ({
    key: p.bucket,
    label: p.bucket.length > 10 ? p.bucket.slice(5) : p.bucket,
    value: p.tracksUsed ?? 0,
  }));

  return (
    <div data-testid="analytics-trends-root">
      <PeriodGranularityControls
        period={period}
        onPeriodChange={setPeriod}
        showGranularity
        granularity={granularity}
        onGranularityChange={setGranularity}
      />

      <AnalyticsQueryState loading={loading} error={error} onRetry={load} isEmpty={isEmpty}>
        {prior && (
          <div style={styles.card} data-testid="analytics-trends-prior">
            <p style={styles.sectionTitle}>Vs prior period</p>
            <div style={styles.metricGrid}>
              <div style={styles.metric}>
                <div style={styles.metricLabel}>Revenue change</div>
                <div style={styles.metricValue}>{formatPercent(prior.revenueChangePercent)}</div>
              </div>
              <div style={styles.metric}>
                <div style={styles.metricLabel}>Adoption change</div>
                <div style={styles.metricValue}>{formatPercent(prior.adoptionChangePercent)}</div>
              </div>
              {prior.usageChangePercent != null ? (
                <div style={styles.metric}>
                  <div style={styles.metricLabel}>Usage change</div>
                  <div style={styles.metricValue}>{formatPercent(prior.usageChangePercent)}</div>
                </div>
              ) : null}
            </div>
          </div>
        )}

        <div style={styles.card}>
          <p style={styles.sectionTitle}>Adoption — new installs by bucket</p>
          {adoptionChart.some((d) => d.value > 0) ? (
            <SimpleColumnChart data={adoptionChart} />
          ) : null}
          <TrendTable
            rows={adoption.map((r) => ({
              bucket: r.bucket,
              a: r.newInstalls ?? '—',
              b: r.cumulative ?? '—',
            }))}
            colA="New installs"
            colB="Cumulative"
          />
        </div>

        <div style={styles.card}>
          <p style={styles.sectionTitle}>Payments — revenue by bucket</p>
          {revenueChart.some((d) => d.value > 0) ? <SimpleColumnChart data={revenueChart} /> : null}
          <table>
            <thead>
              <tr>
                <th>Bucket</th>
                <th>Revenue</th>
                <th>Payment count</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((r) => (
                <tr key={r.bucket}>
                  <td>{r.bucket}</td>
                  <td>{formatUsdFromCents(r.revenue)}</td>
                  <td>{r.paymentCount ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div style={styles.card}>
          <p style={styles.sectionTitle}>Usage — tracks by bucket</p>
          {tracksChart.some((d) => d.value > 0) ? <SimpleColumnChart data={tracksChart} /> : null}
          <table>
            <thead>
              <tr>
                <th>Bucket</th>
                <th>Tracks used</th>
                <th>Tracks granted</th>
              </tr>
            </thead>
            <tbody>
              {usage.map((r) => (
                <tr key={r.bucket}>
                  <td>{r.bucket}</td>
                  <td>{r.tracksUsed ?? '—'}</td>
                  <td>{r.tracksGranted ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </AnalyticsQueryState>
    </div>
  );
}

function TrendTable({
  rows,
  colA,
  colB,
}: {
  rows: { bucket: string; a: string | number; b: string | number }[];
  colA: string;
  colB: string;
}) {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table>
        <thead>
          <tr>
            <th>Bucket</th>
            <th>{colA}</th>
            <th>{colB}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.bucket}>
              <td>{r.bucket}</td>
              <td>{r.a}</td>
              <td>{r.b}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
